<template>
    <layout>
        <a-breadcrumb>
            <a-breadcrumb-item data-cy="breadcrumb-main">
                {{ $t('globals.navigation.header.company_edit.title') }}
            </a-breadcrumb-item>
            <a-breadcrumb-item data-cy="breadcrumb-sub">
                {{ tabTitle(this.default_tab) }}
            </a-breadcrumb-item>
        </a-breadcrumb>

        <div class="shadow-xl settings">
            <a-tabs
                default-active-key="general"
                :active-key="default_tab"
                tab-position="left"
                size="default"
                @change="onTabChange"
                data-cy="tab-container"
            >
                <a-tab-pane key="main" :tab="tabTitle('main')" disabled />
                <a-tab-pane key="general" :tab="tabTitle('general')">
                    <tab-general :company="company" />
                </a-tab-pane>
                <a-tab-pane key="users" :tab="tabTitle('users')">
                    <tab-users :company_id="company_id" ref="UsersTab" />
                </a-tab-pane>
                <a-tab-pane key="active-sessions" :tab="tabTitle('active-sessions')">
                    <tab-active-sessions :company_id="company_id" ref="ActiveSessionsTab" />
                </a-tab-pane>
                <a-tab-pane
                    v-if="company.company_type.toLowerCase() == 'insurer' && isCiaEnabled"
                    key="templates"
                    :tab="tabTitle('templates')"
                >
                    <tab-templates :company_id="company_id" ref="TemplatesTab" />
                </a-tab-pane>
                <a-tab-pane
                    key="upload-history-claims"
                    :tab="tabTitle('upload-history-claims')"
                    v-if="isClaimsVaultEnabled"
                >
                    <tab-upload-history-claims :company_id="company_id" ref="UploadHistoryClaimsTab" />
                </a-tab-pane>
                <a-tab-pane
                    key="upload-history-covers"
                    :tab="tabTitle('upload-history-covers')"
                    v-if="isCoverVaultEnabled"
                >
                    <tab-upload-history-covers :company_id="company_id" ref="UploadHistoryCoversTab" />
                </a-tab-pane>
                <a-tab-pane key="audit-logs" :tab="tabTitle('audit-logs')">
                    <tab-audit-logs :company_id="company_id" ref="AuditLogsTab" />
                </a-tab-pane>
                <a-tab-pane
                    key="intermediaries"
                    v-if="company.company_type.toLowerCase() == 'insurer' && isCiaEnabled"
                    :tab="tabTitle('intermediaries')"
                >
                    <tab-intermediaries ref="IntermediariesTab" :company="company" />
                </a-tab-pane>
                <a-tab-pane key="claims" :tab="tabTitle('claims')">
                    <tab-claims :company_id="company_id" :section_type="'company'" ref="ClaimsTab" />
                </a-tab-pane>
                <a-tab-pane key="claimants" :tab="tabTitle('claimants')" v-if="isClaimsVaultEnabled">
                    <tab-claimants :company_id="company_id" ref="ClaimantsTab" />
                </a-tab-pane>
                <a-tab-pane key="covers" :tab="tabTitle('covers')" v-if="isCoverVaultEnabled">
                    <tab-covers :company_id="company_id" ref="CoversTab" />
                </a-tab-pane>
            </a-tabs>
        </div>
    </layout>
</template>

<script>
import Layout from '@/layouts/Layout.vue';
import TabGeneral from '@/components/tabs/company/General.vue';
import TabUsers from '@/components/tabs/company/Users.vue';
import TabActiveSessions from '@/components/tabs/company/ActiveSessions.vue';
import TabTemplates from '@/components/tabs/company/Templates.vue';
import TabUploadHistoryClaims from '@/components/tabs/company/UploadHistoryClaims.vue';
import TabUploadHistoryCovers from '@/components/tabs/company/UploadHistoryCovers.vue';
import TabAuditLogs from '@/components/tabs/company/AuditLogs.vue';
import TabIntermediaries from '@/components/tabs/Intermediaries.vue';
import TabClaims from '@/components/tabs/Claims.vue';
import TabClaimants from '@/components/tabs/Claimants.vue';
import TabCovers from '@/components/tabs/Covers.vue';

export default {
    components: {
        Layout,
        TabGeneral,
        TabUsers,
        TabActiveSessions,
        TabTemplates,
        TabUploadHistoryClaims,
        TabUploadHistoryCovers,
        TabAuditLogs,
        TabIntermediaries,
        TabClaims,
        TabClaimants,
        TabCovers,
    },
    data() {
        return {
            company_id: this.$route.params.id,
            company: { company_type: '' },
            default_tab: 'general',
        };
    },
    mounted() {
        const tab = this.$route.params.tab;

        this.setTab(tab);
        this.fetch();
    },
    beforeRouteLeave(to, from) {
        let is_edit = this.$refs.IntermediariesTab == undefined ? false : this.$refs.IntermediariesTab.is_edit;

        if (is_edit) {
            return new Promise((resolve) => {
                this.$confirm({
                    title: this.$t('company.nav_modal.title'),
                    content: this.$t('company.nav_modal.content'),
                    onOk() {
                        resolve(true);
                    },
                    onCancel() {
                        resolve(false);
                    },
                });
            });
        }
    },
    computed: {
        isClaimsVaultEnabled() {
            return this.$store.state.user.modules.claimsvault;
        },
        isCoverVaultEnabled() {
            return this.$store.state.user.modules.covervault;
        },
        isCiaEnabled() {
            if (this.default_tab == 'intermediaries' || this.default_tab == 'templates') {
                if (!this.$store.state.user.modules.cia || !this.company.company_type == 'insurer') {
                    this.onTabChange('general');
                }
            }

            if (this.default_tab == 'upload-history-claims') {
                if (!this.isClaimsVaultEnabled) {
                    this.onTabChange('general');
                }
            }

            if (this.default_tab == 'upload-history-covers') {
                if (!this.isCoverVaultEnabled) {
                    this.onTabChange('general');
                }
            }

            if (this.default_tab == 'claims' || this.default_tab == 'claimants' || this.default_tab == 'covers') {
                if (!this.isClaimsVaultEnabled) {
                    this.onTabChange('general');
                }
            }

            return this.$store.state.user.modules.cia;
        },
    },
    methods: {
        setTab(tab) {
            switch (tab) {
                case 'users':
                    if (typeof this.$refs.UsersTab !== 'undefined') {
                        this.$refs.UsersTab.reloadTable();
                    }
                    this.default_tab = 'users';
                    break;
                case 'active-sessions':
                    if (typeof this.$refs.ActiveSessionsTab !== 'undefined') {
                        this.$refs.ActiveSessionsTab.reload();
                    }
                    this.default_tab = 'active-sessions';
                    break;
                case 'templates':
                    if (typeof this.$refs.TemplatesTab !== 'undefined') {
                        this.$refs.TemplatesTab.reload();
                    }
                    this.default_tab = 'templates';
                    break;
                case 'upload-history-claims':
                    if (typeof this.$refs.UploadHistoryClaimsTab !== 'undefined') {
                        this.$refs.UploadHistoryClaimsTab.reload();
                    }
                    this.default_tab = 'upload-history-claims';
                    break;
                case 'upload-history-covers':
                    if (typeof this.$refs.UploadHistoryCoversTab !== 'undefined') {
                        this.$refs.UploadHistoryCoversTab.reload();
                    }
                    this.default_tab = 'upload-history-covers';
                    break;
                case 'audit-logs':
                    if (typeof this.$refs.AuditLogsTab !== 'undefined') {
                        this.$refs.AuditLogsTab.reload();
                    }
                    this.default_tab = 'audit-logs';
                    break;
                case 'intermediaries':
                    if (typeof this.$refs.IntermediariesTab !== 'undefined') {
                        this.$refs.IntermediariesTab.reload();
                    }
                    this.default_tab = 'intermediaries';
                    break;
                case 'claims':
                    if (typeof this.$refs.ClaimsTab !== 'undefined') {
                        this.$refs.ClaimsTab.reload();
                    }
                    this.default_tab = 'claims';
                    break;
                case 'claimants':
                    if (typeof this.$refs.ClaimantsTab !== 'undefined') {
                        this.$refs.ClaimantsTab.reload();
                    }
                    this.default_tab = 'claimants';
                    break;
                case 'covers':
                    if (typeof this.$refs.CoversTab !== 'undefined') {
                        this.$refs.CoversTab.reload();
                    }
                    this.default_tab = 'covers';
                    break;
                default:
                    this.default_tab = 'general';
                    break;
            }
        },
        tabTitle(key) {
            switch (key) {
                case 'main':
                    return this.$t('company.tabs.main');
                case 'general':
                    return this.$t('company.tabs.general');
                case 'users':
                    return this.$t('company.tabs.users');
                case 'active-sessions':
                    return this.$t('company.tabs.active_sessions');
                case 'templates':
                    return this.$t('company.tabs.templates');
                case 'upload-history-claims':
                    return this.$t('company.tabs.upload_history_claims');
                case 'upload-history-covers':
                    return this.$t('company.tabs.upload_history_covers');
                case 'audit-logs':
                    return this.$t('company.tabs.audit_logs');
                case 'intermediaries':
                    return this.$t('company.tabs.intermediaries');
                case 'claims':
                    return this.$t('company.tabs.claims');
                case 'claimants':
                    return this.$t('company.tabs.claimants');
                case 'covers':
                    return this.$t('company.tabs.covers');
            }
        },
        onTabChange(key) {
            const vm = this;
            let is_edit = this.$refs.IntermediariesTab == undefined ? false : this.$refs.IntermediariesTab.is_edit;

            if (is_edit) {
                this.$confirm({
                    title: this.$t('company.nav_modal.title'),
                    content: this.$t('company.nav_modal.content'),
                    onOk() {
                        vm.$refs.IntermediariesTab.fetch();
                        vm.setTab(key);
                        vm.fetch();

                        vm.$router.push({
                            name: 'edit-companies-tab',
                            params: {
                                id: vm.company_id,
                                tab: key,
                            },
                        });
                    },
                    onCancel() {
                        return false;
                    },
                });
            } else {
                this.setTab(key);
                this.fetch();

                this.$router.push({
                    name: 'edit-companies-tab',
                    params: {
                        id: this.company_id,
                        tab: key,
                    },
                });
            }
        },
        fetch() {
            const id = this.$route.params.id;

            this.$http
                .get('/api/companies/' + id)
                .then((response) => {
                    let companyData = {
                        ...response.data.data,
                        is_active: response.data.data.is_active == 1 ? true : false,
                        is_no_lock_out: response.data.data.is_no_lock_out == 1 ? true : false,
                        is_api_access: response.data.data.is_api_access == 1 ? true : false,
                        is_ip_address_routing: response.data.data.is_ip_address_routing == 1 ? true : false,
                        allow_multi_ip_sessions: response.data.data.allow_multi_ip_sessions == 1 ? true : false,
                        is_replace_tp: response.data.data.is_replace_tp == 1 ? true : false,
                        is_hide_claim_description: response.data.data.is_hide_claim_description == 1 ? true : false,
                        is_multi_association_link: response.data.data.is_multi_association_link == 1 ? true : false,
                        is_data_provider: response.data.data.is_data_provider == 1 ? true : false,
                        is_enforce_filename_restriction:
                            response.data.data.is_enforce_filename_restriction == 1 ? true : false,
                        is_enforce_column_count_restriction:
                            response.data.data.is_enforce_column_count_restriction == 1 ? true : false,
                        update_upload_date_on_new_claims:
                            response.data.data.update_upload_date_on_new_claims == 1 ? true : false,
                        is_module_claimsvault_enabled:
                            response.data.data.is_module_claimsvault_enabled == 1 ? true : false,
                        is_module_covervault_enabled:
                            response.data.data.is_module_covervault_enabled == 1 ? true : false,
                        link_association_ids:
                            response.data.data.link_association_ids === null ||
                            response.data.data.link_association_ids === ''
                                ? []
                                : _.split(response.data.data.link_association_ids, ','),
                        link_company_ids:
                            response.data.data.link_company_ids === null || response.data.data.link_company_ids === ''
                                ? []
                                : _.split(response.data.data.link_company_ids, ','),
                        claim_last_imported_at:
                            response.data.data.claim_last_imported_at === null
                                ? this.$t('company.general.view.never')
                                : this.$dayjs(response.data.data.claim_last_imported_at).format(
                                      'MMMM Do, YYYY [at] hh:mm:ss A',
                                  ),
                        cover_last_imported_at:
                            response.data.data.cover_last_imported_at === null
                                ? this.$t('company.general.view.never')
                                : this.$dayjs(response.data.data.cover_last_imported_at).format(
                                      'MMMM Do, YYYY [at] hh:mm:ss A',
                                  ),
                        deactivation_date:
                            response.data.data.deactivation_date === null
                                ? null
                                : this.$dayjs(response.data.data.deactivation_date).format('MMMM Do, YYYY'),
                        email_address:
                            response.data.data.email_address === null || response.data.data.email_address === ''
                                ? []
                                : _.split(response.data.data.email_address, ','),
                        is_tfa_enabled: response.data.data.is_tfa_enabled === 1 ? true : false,
                        is_association_tfa_enabled: response.data.data.is_association_tfa_enabled === 1 ? true : false,
                        send_import_claim_email: response.data.data.send_import_claim_email === 1 ? true : false,
                        send_import_cover_email: response.data.data.send_import_cover_email === 1 ? true : false,
                        email_rejected_records_on_upload_claims:
                            response.data.data.email_rejected_records_on_upload_claims == 1 ? true : false,
                        email_rejected_records_on_upload_covers:
                            response.data.data.email_rejected_records_on_upload_covers == 1 ? true : false,
                        allow_full_association_cover_search:
                            response.data.data.allow_full_association_cover_search === 1 ? true : false,
                        enable_advanced_claims_analytics:
                            response.data.data.enable_advanced_claims_analytics === 1 ? true : false,
                        enable_advanced_cover_analytics:
                            response.data.data.enable_advanced_cover_analytics === 1 ? true : false,
                    };
                    this.company = { ...this.company, ...companyData };
                })
                .catch((error) => {
                    if (error.response.status == 404) {
                        this.$router.push({
                            name: 'not-found',
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCVW01' }),
                        });
                    }
                });
        },
    },
};
</script>
